import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const AudienceContext = createContext();

export const AUDIENCE_TYPES = {
  B2B: 'corporate',
  B2C: 'individual',
  NONE: null
};

export const AudienceProvider = ({ children }) => {
  const [audience, setAudience] = useState(() => {
    const saved = localStorage.getItem('coachmeupp_audience');
    return saved || AUDIENCE_TYPES.NONE;
  });
  
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    if (!audience) {
      const timer = setTimeout(() => setShowSelector(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [audience]);

  const selectAudience = useCallback((type) => {
    setAudience(type);
    localStorage.setItem('coachmeupp_audience', type);
    setShowSelector(false);
  }, []);

  const resetAudience = useCallback(() => {
    setAudience(AUDIENCE_TYPES.NONE);
    localStorage.removeItem('coachmeupp_audience');
    setShowSelector(true);
  }, []);

  const openSelector = useCallback(() => {
    setShowSelector(true);
  }, []);

  const closeSelector = useCallback(() => {
    setShowSelector(false);
  }, []);

  const isB2B = audience === AUDIENCE_TYPES.B2B;
  const isB2C = audience === AUDIENCE_TYPES.B2C;
  const hasSelected = audience !== AUDIENCE_TYPES.NONE;

  const value = useMemo(() => ({
    audience,
    selectAudience,
    resetAudience,
    showSelector,
    openSelector,
    closeSelector,
    isB2B,
    isB2C,
    hasSelected
  }), [audience, selectAudience, resetAudience, showSelector, openSelector, closeSelector, isB2B, isB2C, hasSelected]);

  return (
    <AudienceContext.Provider value={value}>
      {children}
    </AudienceContext.Provider>
  );
};

export const useAudience = () => {
  const context = useContext(AudienceContext);
  if (!context) {
    throw new Error('useAudience must be used within an AudienceProvider');
  }
  return context;
};

export default AudienceContext;
